import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";
import { Department } from "../Departments";

interface DepartmentFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  department: Department | null;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: Function;
}

const DepartmentFormDialog: React.FC<DepartmentFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  department,
  companyId,
  getToken,
  eventListener,
}) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#2196f3");

  useEffect(() => {
    if (department) {
      setName(department.name || "");
      setDescription(department.description || "");
      setColor(department.color || "#2196f3");
    } else {
      setName("");
      setDescription("");
      setColor("#2196f3");
    }
  }, [department, open]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      eventListener({
        type: "SET_ERROR",
        error: "Department name is required",
      });
      return;
    }

    setLoading(true);
    const currentToken = await getToken();

    const payload = {
      companyId,
      name: name.trim(),
      description: description.trim() || null,
      color,
    };

    try {
      if (department) {
        // Update existing department
        await axios.put(
          `${hostname}/departments/${department.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          }
        );
        eventListener({
          type: "SET_SUCCESS",
          message: "Department updated successfully",
        });
      } else {
        // Create new department
        await axios.post(
          `${hostname}/departments`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          }
        );
        eventListener({
          type: "SET_SUCCESS",
          message: "Department created successfully",
        });
      }
      onSuccess();
    } catch (err: any) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {department ? "Edit Department" : "Add Department"}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Department Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Describe the purpose of this department..."
          />
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Color
            </Typography>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              style={{
                width: "100%",
                height: 40,
                border: "1px solid #ccc",
                borderRadius: 4,
                cursor: "pointer",
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="text"
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
        >
          {department ? "Update" : "Create"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DepartmentFormDialog;
